import moment from 'moment';

import { useEffect, useRef, useState } from 'react';

import { Offcanvas } from 'react-bootstrap';

import { Link, useParams } from 'react-router-dom';

import { ToastContainer, toast } from 'react-toastify';

import 'react-toastify/dist/ReactToastify.css';

import defaultAvatar from '../../assets/user.png';
import InboxMessageItem from '../../components/InboxMessageItem';
import InboxMessageItem2 from '../../components/InboxMessageItem2';
import MemberItem from '../../components/MemberItem';
import Nav from '../../components/Nav';
import ReservationOwnerPanel from '../../components/ReservationOwnerPanel';

import '../../styles/styles.css';
import './mc-inbox.css';

import { useAxios } from '../../utils/hook/useAxios';
import { useUser } from '../../utils/hook/UserContext';
import socket from '../../utils/socket';

function InboxMessagePage() {
  const [conversations, setConversations] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [Message, setMessage] = useState('');

  const [missingInviteDocs, setMissingInviteDocs] = useState(false);

  const [ReservationData, setReservationData] = useState(null);

  const [askProlongation, setAskProlongation] = useState([]);
  const [hasProlongation, setHasProlongation] = useState(false);

  const [membresOpen, setMembresOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const { user, loading, isAuthenticated } = useUser();

  const userData = user;

  const axios = useAxios();

  const { messageid } = useParams();

  /*
   * Conteneur scrollable des messages.
   * On scrolle ce conteneur lui-même (et non scrollIntoView) pour ne pas
   * faire défiler les parents en `overflow: hidden` (ce qui décalait la page).
   */
  const messagesScrollRef = useRef(null);

  /*
   * Permet d'avoir toujours les conversations les plus récentes
   * dans les handlers Socket.IO.
   */
  const conversationsRef = useRef([]);

  /*
   * ---------------------------------------------------------
   * AUTO-SCROLL
   * ---------------------------------------------------------
   */
  useEffect(() => {
    const el = messagesScrollRef.current;

    if (!el) return;

    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [conversations]);

  /*
   * ---------------------------------------------------------
   * SYNCHRONISATION DE LA REF
   * ---------------------------------------------------------
   */
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  /*
   * ---------------------------------------------------------
   * SOCKET.IO
   * ---------------------------------------------------------
   */
  useEffect(() => {
    if (!messageid) return;

    const handleNewMessage = (msg) => {
      const enriched = {
        ...msg,
        Reservation: msg.Reservation || conversationsRef.current[0]?.Reservation || null,
      };

      setConversations((prev) => {
        /*
         * Évite les doublons.
         */
        if (
          msg.conversationId &&
          prev.some((conversation) => conversation.conversationId === msg.conversationId)
        ) {
          return prev;
        }

        return [...prev, enriched];
      });
    };

    const joinRoom = () => {
      socket.emit('join-room', messageid);
    };

    /*
     * Si Socket.IO est déjà connecté,
     * on rejoint directement la room.
     */
    if (socket.connected) {
      joinRoom();
    } else {
      socket.connect();
      socket.once('connect', joinRoom);
    }

    socket.on('new-message', handleNewMessage);

    return () => {
      socket.emit('leave-room', messageid);

      socket.off('new-message', handleNewMessage);

      socket.off('connect', joinRoom);
    };
  }, [messageid]);

  /*
   * ---------------------------------------------------------
   * CHARGEMENT INITIAL
   * conversations
   * réservations
   * réservation courante
   * ---------------------------------------------------------
   */
  useEffect(() => {
    if (!messageid || !userData?.id) return;

    let cancelled = false;

    const fetchAll = async () => {
      try {
        /*
         * ---------------------------------------------------
         * 1. Conversations
         * ---------------------------------------------------
         */
        const response = await axios.get(`/conversations/${messageid}`);

        if (cancelled) return;

        const conversationData = Array.isArray(response.data) ? response.data : [];

        setConversations(conversationData);

        /*
         * Aucune conversation.
         */
        if (conversationData.length === 0) {
          console.warn('Aucune conversation trouvée pour :', messageid);

          setReservationData(null);
          setMissingInviteDocs(false);

          return;
        }

        /*
         * ---------------------------------------------------
         * 2. Récupérer le reservationId
         * ---------------------------------------------------
         */
        const reservationId =
          conversationData[0]?.reservationId || conversationData[0]?.Reservation?.reservationId;

        if (!reservationId) {
          console.error(
            'Impossible de trouver reservationId dans la conversation :',
            conversationData[0]
          );

          return;
        }

        console.log('Reservation ID trouvé :', reservationId);

        /*
         * ---------------------------------------------------
         * 3. Documents de l'invité
         * ---------------------------------------------------
         */
        const invite = conversationData[0]?.Reservation?.Invite;

        setMissingInviteDocs(!invite?.PermisConduireDoc || !invite?.PieceIdentityDoc);

        /*
         * ---------------------------------------------------
         * 4. Réservations de l'utilisateur
         * ---------------------------------------------------
         */
        const resp = await axios.get(`/reservations/getbydriverid/${userData.id}`);

        if (cancelled) return;

        if (Array.isArray(resp.data)) {
          const sortedReservations = [...resp.data].sort(
            (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
          );

          setReservations(sortedReservations);
        } else {
          setReservations([]);
        }

        /*
         * ---------------------------------------------------
         * 5. Récupérer la réservation courante
         * ---------------------------------------------------
         */
        const respResa = await axios.get(`/reservations/getbyid/${reservationId}`);

        if (cancelled) return;

        console.log('Réservation récupérée :', respResa.data);

        if (respResa.data) {
          setReservationData(respResa.data);

          /*
           * Si l'API de réservation contient les données
           * de l'invité, on recalcule également les documents.
           */
          const reservationInvite = respResa.data?.Invite || respResa.data?.Reservation?.Invite;

          if (reservationInvite) {
            setMissingInviteDocs(
              !reservationInvite.PermisConduireDoc || !reservationInvite.PieceIdentityDoc
            );
          }
        }
      } catch (error) {
        if (cancelled) return;

        console.error(
          'Erreur lors du chargement de la conversation/réservation :',
          error?.response?.data || error
        );
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [axios, messageid, userData?.id]);

  /*
   * ---------------------------------------------------------
   * DEMANDES DE PROLONGATION / MODIFICATION
   * ---------------------------------------------------------
   */
  useEffect(() => {
    if (!messageid) return;

    let cancelled = false;

    const fetchProlongation = async () => {
      try {
        const resp = await axios.get(`/askupdateresa/reservation/${messageid}`);

        if (cancelled) return;

        const data = Array.isArray(resp.data) ? resp.data : [];

        setAskProlongation(data);
        setHasProlongation(data.length > 0);
      } catch (error) {
        if (cancelled) return;

        console.error(
          'Erreur récupération demande de modification :',
          error?.response?.data || error
        );

        setAskProlongation([]);
        setHasProlongation(false);
      }
    };

    fetchProlongation();

    return () => {
      cancelled = true;
    };
  }, [messageid]);

  /*
   * ---------------------------------------------------------
   * SYNCHRONISATION CALENDRIER INVITÉ
   * ---------------------------------------------------------
   */
  useEffect(() => {
    if (!ReservationData || ReservationData.status !== 'accepted') {
      return;
    }

    if (
      !ReservationData.driverInviteId ||
      !ReservationData.startDate ||
      !ReservationData.endDate ||
      !ReservationData.reservationId
    ) {
      return;
    }

    axios
      .put(
        `/reservations/updatebyInviteidDates/${ReservationData.driverInviteId}?startDate=${ReservationData.startDate}&endDate=${ReservationData.endDate}&reservationIdToExclude=${ReservationData.reservationId}`
      )
      .catch((error) => {
        console.error('Erreur synchronisation calendrier :', error);
      });
  }, [ReservationData]);

  /*
   * ---------------------------------------------------------
   * REFRESH CONVERSATIONS
   * ---------------------------------------------------------
   */
  const refreshConversations = async () => {
    if (!ReservationData?.reservationId) {
      console.warn('Impossible de rafraîchir les conversations : ReservationData est null.');

      return;
    }

    try {
      const response = await axios.get(`/conversations/${ReservationData.reservationId}`);

      setConversations(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur refresh conversations :', error?.response?.data || error);
    }
  };

  /*
   * ---------------------------------------------------------
   * ENVOYER MESSAGE
   * ---------------------------------------------------------
   */
  function handleSendMessage() {
    if (!Message.trim()) return;

    if (!ReservationData?.reservationId) {
      console.warn("Impossible d'envoyer le message : réservation introuvable.");

      return;
    }

    if (!userData?.id) {
      console.warn("Impossible d'envoyer le message : utilisateur introuvable.");

      return;
    }

    const msgText = Message;

    setMessage('');

    /*
     * driverHoteId = 0
     * driverInviteId = 1
     */
    const isHote = ReservationData?.driverHoteId === userData?.id;

    const myRole = isHote ? 0 : 1;

    axios
      .post('/conversations', {
        authorDriverRole: myRole,
        driversId: userData.id,
        sentTime: new Date(),
        reservationId: ReservationData.reservationId,
        message: msgText,
      })
      .then(async () => {
        await refreshConversations();
      })
      .catch((error) => {
        console.error('Erreur envoi message :', error?.response?.data || error);

        setMessage(msgText);
      });
  }

  /*
   * ---------------------------------------------------------
   * ACCEPTER RESERVATION
   * ---------------------------------------------------------
   */
  function handleAccepterReservation() {
    if (!ReservationData?.reservationId) return;

    if (!ReservationData?.driverHoteId) {
      console.warn('driverHoteId introuvable.');

      return;
    }

    axios
      .post('/conversations', {
        authorDriverRole: 0,
        driversId: ReservationData.driverHoteId,
        sentTime: new Date(),
        reservationId: ReservationData.reservationId,
        message:
          'Le hote a accepté votre demande de paiement, vous pouvez entamer la discussion et passer au paiement!',
      })
      .then(async () => {
        await refreshConversations();

        const response = await axios.put(`/reservations/${ReservationData.reservationId}`, {
          status: 'accepted',
        });

        if (response.data) {
          setReservationData(response.data);
        }
      })
      .catch((error) => {
        console.error("Erreur lors de l'acceptation :", error?.response?.data || error);
      });
  }

  /*
   * ---------------------------------------------------------
   * ANNULER / REFUSER RESERVATION
   * ---------------------------------------------------------
   */
  function handleAnnulerReservation() {
    if (!ReservationData?.reservationId) return;

    const isConfirmed = window.confirm('Êtes-vous sûr de vouloir annuler ce voyage ?');

    if (!isConfirmed) return;

    axios
      .post('/conversations', {
        authorDriverRole: 1,
        driversId: ReservationData.driverHoteId,
        sentTime: new Date(),
        reservationId: ReservationData.reservationId,
        message: "L'invité a annulé la réservation",
      })
      .then(async () => {
        await refreshConversations();

        try {
          await axios.put(`/reservations/${ReservationData.reservationId}`, {
            status: 'rejected',
          });

          await axios.put(`/reservationsGain/${ReservationData.reservationId}`, {
            type: '1',
          });

          /*
           * Recharge les données au lieu de recharger
           * brutalement toute la page.
           */
          await fetchReservation();
        } catch (error) {
          console.error("Erreur lors de l'annulation :", error?.response?.data || error);
        }
      })
      .catch((error) => {
        console.error("Erreur lors de l'annulation :", error?.response?.data || error);
      });
  }

  /*
   * ---------------------------------------------------------
   * ACCEPTER MODIFICATION RESERVATION
   * ---------------------------------------------------------
   */
  function handleAccepterUpdateReservation() {
    if (!ReservationData?.reservationId) return;

    if (!askProlongation[0]) {
      toast.error('Aucune demande de modification disponible.');

      return;
    }

    axios
      .post('/conversations', {
        authorDriverRole: 0,
        driversId: ReservationData.driverHoteId,
        sentTime: new Date(),
        reservationId: ReservationData.reservationId,
        message: 'Le hote a accepté votre demande de modification de locations',
      })
      .then(async () => {
        await refreshConversations();

        const response = await axios.put(`/reservations/${ReservationData.reservationId}`, {
          startDate: askProlongation[0]?.startDate,
          endDate: askProlongation[0]?.endDate,
        });

        if (response.data) {
          setReservationData(response.data);
        }

        setAskProlongation([]);
        setHasProlongation(false);

        toast.success('Location a été modifiée avec succès');
      })
      .catch((error) => {
        console.error('Erreur acceptation modification :', error?.response?.data || error);
      });
  }

  /*
   * ---------------------------------------------------------
   * REFUSER MODIFICATION RESERVATION
   * ---------------------------------------------------------
   */
  function handleRefuserUpdateReservation() {
    if (!ReservationData?.reservationId) return;

    axios
      .post('/conversations', {
        authorDriverRole: 0,
        driversId: ReservationData.driverHoteId,
        sentTime: new Date(),
        reservationId: ReservationData.reservationId,
        message:
          "Le hote a décliné votre demande de modification de locations. Ce n'est pas possible.",
      })
      .then(async () => {
        await refreshConversations();

        setAskProlongation([]);
        setHasProlongation(false);
      })
      .catch((error) => {
        console.error('Erreur refus modification :', error?.response?.data || error);
      });
  }

  /*
   * ---------------------------------------------------------
   * RECUPERER RESERVATION
   * ---------------------------------------------------------
   */
  const fetchReservation = async () => {
    if (!ReservationData?.reservationId) {
      console.warn('Impossible de récupérer la réservation : reservationId absent.');

      return;
    }

    try {
      const response = await axios.get(`/reservations/getbyid/${ReservationData.reservationId}`);

      if (response.data) {
        setReservationData(response.data);

        const invite = response.data?.Invite || response.data?.Reservation?.Invite;

        if (invite) {
          setMissingInviteDocs(!invite.PermisConduireDoc || !invite.PieceIdentityDoc);
        }
      }
    } catch (error) {
      console.error('Erreur lors du refresh réservation :', error?.response?.data || error);
    }
  };

  /*
   * ---------------------------------------------------------
   * NOM DE L'INVITÉ
   * ---------------------------------------------------------
   *
   * On supporte les deux structures :
   *
   * ReservationData.Invite
   *
   * ou
   *
   * ReservationData.Reservation.Invite
   */
  const invite = ReservationData?.Invite || ReservationData?.Reservation?.Invite;

  const inviteeName = invite?.firstName
    ? `${invite.firstName} ${invite.lastName || ''}`.trim()
    : '';

  const resaStart = ReservationData?.startDate || ReservationData?.Reservation?.startDate;
  const resaEnd = ReservationData?.endDate || ReservationData?.Reservation?.endDate;

  /*
   * ---------------------------------------------------------
   * DEMANDE DE MODIFICATION (partagée : offcanvas + panneau desktop)
   * ---------------------------------------------------------
   * Avant, elle n'était visible que dans l'offcanvas (écrans < lg) :
   * sur desktop l'hôte ne pouvait ni la voir ni l'accepter.
   */
  const renderChangeRequest = (onDone) =>
    hasProlongation ? (
      <div className="mc-ib-change">
        <h4 className="mc-ib-change-title">
          <i className="bi bi-clock-history" aria-hidden="true"></i>
          Demande de modification en attente
        </h4>

        <div className="mc-ib-change-row">
          <p className="mc-ib-change-label">Date de début</p>
          <div className="mc-ib-change-dates">
            <span className="mc-ib-chip mc-ib-chip--old">
              {resaStart ? moment(resaStart).calendar() : '-'}
            </span>
            <i className="bi bi-arrow-right" aria-hidden="true"></i>
            <span className="mc-ib-chip mc-ib-chip--new">
              {askProlongation[0]?.startDate
                ? moment(askProlongation[0].startDate).calendar()
                : '-'}
            </span>
          </div>
        </div>

        <div className="mc-ib-change-row">
          <p className="mc-ib-change-label">Date de fin</p>
          <div className="mc-ib-change-dates">
            <span className="mc-ib-chip mc-ib-chip--old">
              {resaEnd ? moment(resaEnd).calendar() : '-'}
            </span>
            <i className="bi bi-arrow-right" aria-hidden="true"></i>
            <span className="mc-ib-chip mc-ib-chip--new">
              {askProlongation[0]?.endDate ? moment(askProlongation[0].endDate).calendar() : '-'}
            </span>
          </div>
        </div>

        {askProlongation[0]?.motif && (
          <p className="mc-ib-change-motif">
            <strong>Motif :</strong> {askProlongation[0].motif}
          </p>
        )}

        <div className="mc-ib-change-actions">
          <button
            type="button"
            className="mc-btn mc-btn--danger-soft mc-btn--sm"
            onClick={() => {
              handleRefuserUpdateReservation();
              onDone?.();
            }}
          >
            <i className="bi bi-x-lg" aria-hidden="true"></i>
            Décliner
          </button>

          {missingInviteDocs ? (
            <span className="mc-ib-chip mc-ib-chip--warn">Docs manquants</span>
          ) : (
            <button
              type="button"
              className="mc-btn mc-btn--primary mc-btn--sm"
              onClick={() => {
                handleAccepterUpdateReservation();
                onDone?.();
              }}
            >
              <i className="bi bi-check-lg" aria-hidden="true"></i>
              Accepter
            </button>
          )}
        </div>
      </div>
    ) : (
      <div className="mc-ib-none">
        <i className="bi bi-check2-circle" aria-hidden="true"></i>
        Aucune demande de modification en attente.
      </div>
    );

  /*
   * ---------------------------------------------------------
   * RENDER
   * ---------------------------------------------------------
   */

  return (
    <>
      <ToastContainer />

      {/* =====================================================
          OFFCANVAS : CONVERSATIONS MOBILE
      ===================================================== */}

      <Offcanvas
        show={membresOpen}
        onHide={() => setMembresOpen(false)}
        placement="start"
        className="mc-ib-offcanvas"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <i className="bi bi-chat-dots" aria-hidden="true"></i>
            Conversations
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body className="p-0">
          <ul className="mc-ib-threads">
            {reservations.map((conv) => (
              <MemberItem
                key={conv.reservationId}
                data={conv}
                onSelect={() => setMembresOpen(false)}
              />
            ))}
          </ul>
        </Offcanvas.Body>
      </Offcanvas>

      {/* =====================================================
          OFFCANVAS : DETAILS VOYAGE
      ===================================================== */}

      <Offcanvas
        show={detailsOpen}
        onHide={() => setDetailsOpen(false)}
        placement="end"
        className="mc-ib-offcanvas mc-ib-offcanvas--wide"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <i className="bi bi-info-circle" aria-hidden="true"></i>
            Détails du voyage
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          {ReservationData && (
            <ReservationOwnerPanel
              reservation={ReservationData}
              onReservationUpdated={fetchReservation}
            />
          )}

          {renderChangeRequest(() => setDetailsOpen(false))}

          <Link
            to={`/reservation/${messageid}`}
            className="mc-btn mc-btn--secondary mc-btn--block mc-ib-detail-link"
          >
            <i className="bi bi-eye" aria-hidden="true"></i>
            Voir la réservation complète
          </Link>
        </Offcanvas.Body>
      </Offcanvas>

      {/* =====================================================
          LAYOUT PRINCIPAL
      ===================================================== */}

      <div className="mc-home mc-ib">
        {/* NAV */}

        <div className="mc-ib-nav">
          <div className="container mc-container">
            <Nav />
          </div>
        </div>

        {/* CORPS */}

        <div className={`mc-ib-body${ReservationData ? ' mc-ib-body--panel' : ''}`}>
          {/* =================================================
              SIDEBAR DESKTOP
          ================================================= */}

          <aside className="mc-ib-panel mc-ib-panel--side" aria-label="Conversations">
            <div className="mc-ib-panel-head">
              <Link to="/inbox/messages" className="mc-ib-back">
                <i className="bi bi-arrow-left" aria-hidden="true"></i>
                Messages
              </Link>
              <span className="mc-ib-count" title="Nombre de conversations">
                {reservations.length}
              </span>
            </div>

            <div className="mc-ib-scroll">
              {reservations.length > 0 ? (
                <ul className="mc-ib-threads">
                  {reservations.map((conv) => (
                    <MemberItem key={conv.reservationId} data={conv} />
                  ))}
                </ul>
              ) : (
                <div className="mc-ib-empty">
                  <i className="bi bi-chat-dots" aria-hidden="true"></i>
                  Aucune conversation
                </div>
              )}
            </div>
          </aside>

          {/* =================================================
              ZONE CHAT
          ================================================= */}

          <section className="mc-ib-panel mc-ib-panel--chat" aria-label="Messagerie">
            {/* HEADER */}

            <div className="mc-ib-chat-head">
              <Link
                to="/inbox/messages"
                className="mc-ib-icon-btn mc-ib-only-mobile"
                aria-label="Retour"
              >
                <i className="bi bi-arrow-left"></i>
              </Link>

              <button
                type="button"
                className="mc-ib-icon-btn mc-ib-only-mobile"
                onClick={() => setMembresOpen(true)}
                aria-label="Conversations"
              >
                <i className="bi bi-people"></i>
              </button>

              <div className="mc-ib-chat-who">
                {ReservationData && (
                  <img
                    src={invite?.profile_url || defaultAvatar}
                    alt=""
                    className="mc-ib-avatar mc-ib-avatar--sm"
                  />
                )}

                <div style={{ minWidth: 0 }}>
                  <p className="mc-ib-chat-name">{inviteeName || 'Conversation'}</p>

                  {ReservationData && (
                    <p className="mc-ib-chat-sub">
                      Résa #{ReservationData.reservationId || messageid}
                    </p>
                  )}
                </div>
              </div>

              <button
                type="button"
                className="mc-btn mc-btn--secondary mc-btn--sm mc-ib-only-compact"
                onClick={() => setDetailsOpen(true)}
              >
                <i className="bi bi-info-circle" aria-hidden="true"></i>
                <span>Détails</span>
              </button>
            </div>

            {/* =================================================
                MESSAGES
            ================================================= */}

            <div
              className="mc-ib-scroll mc-ib-messages"
              ref={messagesScrollRef}
              role="log"
              aria-live="polite"
            >
              {conversations.length === 0 && (
                <div className="mc-ib-empty">
                  <i className="bi bi-chat-text" aria-hidden="true"></i>
                  Aucun message pour le moment.
                </div>
              )}

              {conversations.map((conv, i) => {
                const isDateChange =
                  typeof conv.message === 'string' &&
                  (conv.message.includes('modification de locations') ||
                    conv.message.includes('demande de modification'));

                if (isDateChange) {
                  return (
                    <div key={conv.conversationId || i} className="mc-ib-event">
                      <div className="mc-ib-event-card">
                        <i className="bi bi-calendar-event" aria-hidden="true"></i>

                        <strong>{conv.message}</strong>

                        {askProlongation[0] && (
                          <div className="mc-ib-event-dates">
                            <span className="mc-ib-chip">
                              {moment(askProlongation[0].startDate).format('D MMM')}
                            </span>

                            <i className="bi bi-arrow-right" aria-hidden="true"></i>

                            <span className="mc-ib-chip">
                              {moment(askProlongation[0].endDate).format('D MMM')}
                            </span>
                          </div>
                        )}

                        <div className="mc-ib-event-time">
                          {moment(conv.sentTime || conv.createdAt).fromNow()}
                        </div>
                      </div>
                    </div>
                  );
                }

                return conv.authorDriverRole === '1' || conv.authorDriverRole === 1 ? (
                  <InboxMessageItem key={conv.conversationId || i} data={conv} />
                ) : (
                  <InboxMessageItem2 key={conv.conversationId || i} data={conv} />
                );
              })}
            </div>

            {/* =================================================
                ACTIONS RESERVATION
            ================================================= */}

            {ReservationData?.status === 'pending' && (
              <div className="mc-ib-actions">
                {missingInviteDocs && (
                  <div className="mc-ib-alert" role="alert">
                    <i className="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>

                    <span>
                      L'invité n'a pas encore uploadé tous ses documents (permis + pièce
                      d'identité). Vous pouvez quand même refuser, mais pas accepter tant que les
                      docs sont manquants.
                    </span>
                  </div>
                )}

                <div className="mc-ib-actions-row">
                  <span className="mc-ib-actions-text">
                    <i className="bi bi-clock-history" aria-hidden="true"></i>
                    Demande en attente de validation
                  </span>

                  <button
                    type="button"
                    className="mc-btn mc-btn--danger-soft mc-btn--sm"
                    onClick={handleAnnulerReservation}
                  >
                    <i className="bi bi-x-lg" aria-hidden="true"></i>
                    Refuser
                  </button>

                  <button
                    type="button"
                    className="mc-btn mc-btn--primary mc-btn--sm"
                    onClick={handleAccepterReservation}
                    disabled={missingInviteDocs}
                    title={missingInviteDocs ? "Documents de l'invité manquants" : ''}
                  >
                    <i className="bi bi-check-lg" aria-hidden="true"></i>
                    Accepter
                  </button>
                </div>
              </div>
            )}

            {/* =================================================
                RESERVATION REFUSEE
            ================================================= */}

            {ReservationData?.status === 'rejected' && (
              <div className="mc-ib-actions mc-ib-actions--danger">
                <div className="mc-ib-actions-row">
                  <span className="mc-ib-actions-text">
                    <i className="bi bi-x-circle-fill" aria-hidden="true"></i>
                    <span>
                      <strong>Réservation annulée</strong> — Cette demande a été refusée. Aucune
                      action n'est possible.
                    </span>
                  </span>
                </div>
              </div>
            )}

            {/* =================================================
                INPUT MESSAGE
            ================================================= */}

            <form
              className="mc-ib-composer"
              onSubmit={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
            >
              <input
                className="mc-input"
                placeholder="Taper un message..."
                aria-label="Votre message"
                autoComplete="off"
                value={Message}
                onChange={(e) => setMessage(e.target.value)}
              />

              <button
                type="submit"
                className="mc-ib-send"
                disabled={!Message.trim()}
                aria-label="Envoyer"
              >
                <i className="bi bi-send-fill"></i>
              </button>
            </form>
          </section>

          {/* =================================================
              PANEL RESERVATION DESKTOP
          ================================================= */}

          {ReservationData && (
            <aside
              className="mc-ib-panel mc-ib-panel--detail"
              aria-label="Gestion de la réservation"
            >
              <div className="mc-ib-panel-head">
                <h2 className="mc-ib-panel-title">Gestion de la réservation</h2>
              </div>

              <div className="mc-ib-scroll mc-ib-detail-body">
                <ReservationOwnerPanel
                  reservation={ReservationData}
                  onReservationUpdated={fetchReservation}
                />

                {renderChangeRequest()}
              </div>
            </aside>
          )}
        </div>
      </div>
    </>
  );
}

export default InboxMessagePage;
